import manifest from "./manifest.json";
import { characterStats } from "./characterStats";
import { rolesByUniverse } from "./rolesByUniverse";
import { adaptCreatorVerse, creatorVerseLabel } from "@/lib/creatorAdapter";
import { loadCreatorVerseRows, loadRuntimeCreatorVerseRows } from "@/lib/localCreatorStore";

export type CharacterStats = Record<string, number>;

export interface Character {
  id: string;
  name: string;
  verse: string;
  portrait: string;
  stats: CharacterStats;
}

export type StatType = string;

interface ManifestEntry {
  name: string;
  universe: string;
  images: {
    full: string;
    portrait: string;
  };
}

const data = manifest as ManifestEntry[];

const UNIVERSE_TO_ROLE_KEY: Record<string, string> = {
  Naruto: "naruto",
  Bleach: "bleach",
  DragonBall: "dragonball",
  MHA: "mha",
  SoloLeveling: "sololeveling",
  "7DeadlySins": "7deadlysins",
  Onepiece: "onepiece",
  OnePunchMan: "onepunchman",
  blackclover: "blackclover",
  JJK: "jjk",
  DemonSlayer: "demonslayer",
  HunterxHunter: "hunterxhunter",
  Boruto: "boruto",
};

const UNIVERSE_TO_VERSE_LABEL: Record<string, string> = {
  Naruto: "Naruto",
  Bleach: "Bleach",
  DragonBall: "Dragon Ball",
  MHA: "My Hero Academia",
  SoloLeveling: "Solo Leveling",
  "7DeadlySins": "7 Deadly Sins",
  Onepiece: "One Piece",
  OnePunchMan: "One Punch Man",
  blackclover: "Black Clover",
  JJK: "Jujutsu Kaisen",
  DemonSlayer: "Demon Slayer",
  HunterxHunter: "Hunter x Hunter",
  Boruto: "Boruto",
};

const VERSE_TO_ROLE_KEY: Record<string, string> = Object.entries(UNIVERSE_TO_VERSE_LABEL).reduce(
  (acc, [universe, verseLabel]) => {
    const roleKey = UNIVERSE_TO_ROLE_KEY[universe];
    if (roleKey) acc[verseLabel] = roleKey;
    return acc;
  },
  {} as Record<string, string>
);

const VERSE_BACKDROPS: Record<string, string> = {
  "Naruto": "/assets/backdrops/Naruto Backdrop.webp",
  "Bleach": "/assets/backdrops/Bleach Backdrop.webp",
  "Dragon Ball": "/assets/backdrops/Goku and Vegeta Backdrop.webp",
  "My Hero Academia": "/assets/backdrops/mha Backdrop.webp",
  "Solo Leveling": "/assets/backdrops/SoloLeveling Backdrop.webp",
  "7 Deadly Sins": "/assets/backdrops/7DeadlySins Backdrop.webp",
  "One Piece": "/assets/backdrops/Onepiece Backdrop.webp",
  "One Punch Man": "/assets/backdrops/One Punch Man Backdrop.webp",
  "Black Clover": "/assets/backdrops/blackclover Backdrop.webp",
  "Jujutsu Kaisen": "/assets/backdrops/JJK Backdrop.webp",
  "Demon Slayer": "/assets/backdrops/DemonSlayer Backdrop.webp",
  "Hunter x Hunter": "/assets/backdrops/HunterxHunter Backdrop.webp",
  "Boruto": "/assets/backdrops/Boruto Backdrop.webp",
  "Cross Draft": "/assets/backdrops/CrossDraft Backdrop.webp",
};

export const ALL_VERSES_BACKDROP = "/assets/themes/Logo Backdrop.webp";

const RARE_CHARACTER_NAMES = new Set([
  "Joe Jujutsu",
  "John Kaizen",
  "John Rabbit",
  "John Zenin",
]);

const RARE_CHARACTER_WEIGHT = 0.04;
const TROLL_ID_SEPARATOR = "__troll__";
type TrollVariant = "broken" | "minimum";

function mojibakeScore(value: string): number {
  let score = 0;
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (
      (code >= 0x80 && code <= 0x9f) ||
      code === 0xc2 ||
      code === 0xc3 ||
      code === 0xc5 ||
      code === 0xc6 ||
      code === 0xd0 ||
      code === 0xf0
    ) {
      score += 1;
    }
  }
  return score;
}

function repairMojibake(value: string): string {
  if (mojibakeScore(value) === 0) {
    return value;
  }

  const bytes = Uint8Array.from(Array.from(value), (char) => char.charCodeAt(0) & 0xff);
  const decoded = new TextDecoder("utf-8").decode(bytes);

  if (!decoded || decoded === value) {
    return value;
  }

  return mojibakeScore(decoded) <= mojibakeScore(value) ? decoded : value;
}

function clampStat(value: number): number {
  return Math.max(1000, Math.min(9999, Math.round(value)));
}

function toId(universe: string, name: string): string {
  return `${universe}-${name}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function toVerseLabel(universe: string): string {
  return UNIVERSE_TO_VERSE_LABEL[universe] ?? universe;
}

function makeTrollCharacterId(baseId: string, variant: TrollVariant): string {
  return `${baseId}${TROLL_ID_SEPARATOR}${variant}`;
}

function parseTrollCharacterId(id: string): { baseId: string; variant: TrollVariant | null } {
  const separatorIndex = id.indexOf(TROLL_ID_SEPARATOR);
  if (separatorIndex < 0) {
    return { baseId: id, variant: null };
  }

  const baseId = id.slice(0, separatorIndex);
  const variantValue = id.slice(separatorIndex + TROLL_ID_SEPARATOR.length);
  if (variantValue === "broken" || variantValue === "minimum") {
    return { baseId, variant: variantValue };
  }

  return { baseId: id, variant: null };
}

function buildMinimumStats(stats: CharacterStats): CharacterStats {
  return Object.keys(stats).reduce((acc, key) => {
    acc[key] = 1000;
    return acc;
  }, {} as CharacterStats);
}

function applyTrollVariant(character: Character, variant: TrollVariant): Character {
  return {
    ...character,
    id: makeTrollCharacterId(character.id, variant),
    stats: variant === "minimum" ? buildMinimumStats(character.stats) : { ...character.stats },
  };
}

function lockTrollRoll(character: Character): Character {
  if (!RARE_CHARACTER_NAMES.has(character.name)) {
    return character;
  }

  const variant: TrollVariant = Math.random() < 0.5 ? "broken" : "minimum";
  return applyTrollVariant(character, variant);
}

function toStatIcon(role: string): string {
  const tokens = role
    .replace(/[()]/g, "")
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean);

  if (tokens.length === 0) {
    return "STAT";
  }

  if (tokens.length === 1) {
    return tokens[0].slice(0, 3).toUpperCase();
  }

  return tokens
    .slice(0, 3)
    .map((token) => token[0].toUpperCase())
    .join("");
}

function buildCharacterStats(universe: string, roleMap: Record<string, number>): CharacterStats {
  const roleKey = UNIVERSE_TO_ROLE_KEY[universe];
  const roles = roleKey ? rolesByUniverse[roleKey] ?? [] : [];

  // Use only universe-defined roles and values from characterStats.
  return roles.reduce((acc, role) => {
    acc[role] = clampStat(roleMap[role] ?? 1000);
    return acc;
  }, {} as CharacterStats);
}

const ALL_CHARACTERS: Character[] = data
  .flatMap((item) => {
    const repairedName = repairMojibake(item.name);
    const statsName = characterStats[repairedName] ? repairedName : item.name;
    const stats = characterStats[statsName];

    if (!stats) {
      return [];
    }

    return [{
      id: toId(item.universe, statsName),
      name: statsName,
      verse: toVerseLabel(item.universe),
      portrait: repairMojibake(item.images.portrait),
      stats: buildCharacterStats(item.universe, stats),
    }];
  });

export const CHARACTERS: Record<string, Character[]> = ALL_CHARACTERS.reduce((acc, character) => {
  if (!acc[character.verse]) {
    acc[character.verse] = [];
  }
  acc[character.verse].push(character);
  return acc;
}, {} as Record<string, Character[]>);

const CHARACTER_BY_ID: Record<string, Character> = ALL_CHARACTERS.reduce((acc, character) => {
  acc[character.id] = character;
  return acc;
}, {} as Record<string, Character>);

export const ALL_STATS: StatType[] = [...new Set(Object.values(rolesByUniverse).flat())];

export const STAT_LABELS: Record<StatType, { label: string; icon: string }> = ALL_STATS.reduce(
  (acc, role) => {
    // Bleach's Resurrección and Vollständig roles are stored without the accent
    // (shared with Anime Draft's stat data); show them properly.
    const label = role === "Resurreccion" ? "Resurrección" : role === "Vollstandig" ? "Vollständig" : role;
    acc[role] = { label, icon: toStatIcon(role) };
    return acc;
  },
  {} as Record<StatType, { label: string; icon: string }>
);

// Cross Draft: every official character in one pool. Characters keep the stats from their own
// verse, so battles use only the roles every verse has.
export const CROSS_DRAFT_VERSE = 'Cross Draft';

const CROSS_DRAFT_ROLES: string[] = (() => {
  const lists = [...new Set(Object.values(VERSE_TO_ROLE_KEY))].map((key) => rolesByUniverse[key] ?? []);
  if (!lists.length) return [];
  return lists[0].filter((role) => lists.every((list) => list.includes(role)));
})();

export function getVerseRoles(verse?: string | null): string[] {
  if (!verse) return [];
  if (verse === CROSS_DRAFT_VERSE) return CROSS_DRAFT_ROLES;
  const creatorVerse = getCreatorVerseByLabel(verse);
  if (creatorVerse) return creatorVerse.roles;
  const roleKey = VERSE_TO_ROLE_KEY[verse];
  return roleKey ? rolesByUniverse[roleKey] ?? [] : [];
}

// The roles battles use: the player's pick for this verse, or all of the verse's roles.
export function getActiveRoles(verse?: string | null, rolesByVerse?: Record<string, string[]> | null): string[] {
  const verseRoles = getVerseRoles(verse);
  const picked = verse ? rolesByVerse?.[verse] : undefined;
  if (!picked?.length) return verseRoles;
  const active = verseRoles.filter((role) => picked.includes(role));
  return active.length ? active : verseRoles;
}

export function getStatsForVerse(verse?: string | null, rolesByVerse?: Record<string, string[]> | null): StatType[] {
  const roles = getActiveRoles(verse, rolesByVerse);
  return roles.length > 0 ? [...roles] : [...ALL_STATS];
}

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function normalizeStat(
  stat: string | null | undefined,
  verse?: string | null
): StatType | null {
  if (!stat) return null;
  if (STAT_LABELS[stat]) return stat;

  const lower = stat.toLowerCase();
  const matched = Object.keys(STAT_LABELS).find((key) => key.toLowerCase() === lower);
  if (matched) return matched;

  const verseRoles = getVerseRoles(verse);
  if (verseRoles.length > 0) {
    const idx = hashString(stat) % verseRoles.length;
    return verseRoles[idx];
  }

  return ALL_STATS.length ? ALL_STATS[hashString(stat) % ALL_STATS.length] : stat;
}

export function getCharacterStatValue(character: Character | null | undefined, stat: StatType): number {
  if (!character) {
    return 0;
  }

  return character.stats[stat] ?? 1000;
}

export function getAllCharacters(): Character[] {
  return [...ALL_CHARACTERS, ...getCreatorCharacters()];
}

export function getCharacterById(id: string): Character | null {
  const { baseId, variant } = parseTrollCharacterId(id);
  const character = CHARACTER_BY_ID[baseId] ?? getCreatorCharacterById(baseId);
  if (!character) {
    return null;
  }

  if (!variant || !RARE_CHARACTER_NAMES.has(character.name)) {
    return character;
  }

  return applyTrollVariant(character, variant);
}

export function getCharactersByIds(ids: string[]): Character[] {
  return ids
    .map((id) => getCharacterById(id))
    .filter((character): character is Character => Boolean(character));
}

// Characters a verse's auctions can draw from, before exclusions.
export function getVersePool(verse?: string | null): Character[] {
  return verse ? getCharactersForVerse(verse) : getAllCharacters();
}

export function generateAuctionPool(count = 5, verse?: string, excludedIds: string[] = []): Character[] {
  const excluded = new Set(excludedIds);
  const pool = getVersePool(verse).filter((character) => !excluded.has(character.id));
  const targetCount = Math.min(count, pool.length);

  if (pool.length <= 1) {
    return pool.slice(0, targetCount);
  }

  const pickWeighted = (items: Character[]): Character => {
    let total = 0;
    const weights = items.map((character) => {
      const weight = RARE_CHARACTER_NAMES.has(character.name) ? RARE_CHARACTER_WEIGHT : 1;
      total += weight;
      return weight;
    });

    let roll = Math.random() * total;
    for (let i = 0; i < items.length; i += 1) {
      roll -= weights[i];
      if (roll <= 0) return items[i];
    }

    return items[items.length - 1];
  };

  const remaining = [...pool];
  const selected: Character[] = [];

  while (selected.length < targetCount && remaining.length > 0) {
    const pick = pickWeighted(remaining);
    selected.push(lockTrollRoll(pick));
    const index = remaining.indexOf(pick);
    if (index >= 0) remaining.splice(index, 1);
  }

  return selected;
}

export function getRandomStat(options?: {
  characters?: Character[];
  verse?: string;
  exclude?: StatType | null;
  rolesByVerse?: Record<string, string[]> | null;
}): StatType {
  const exclude = options?.exclude ?? null;
  const verseRoles = getActiveRoles(options?.verse, options?.rolesByVerse);

  if (verseRoles.length > 0) {
    const pool = exclude ? verseRoles.filter((role) => role !== exclude) : verseRoles;
    const finalPool = pool.length > 0 ? pool : verseRoles;
    return finalPool[Math.floor(Math.random() * finalPool.length)];
  }

  if (options?.characters && options.characters.length > 0) {
    const shared = options.characters
      .map((character) => new Set(Object.keys(character.stats)))
      .reduce<Set<string> | null>((intersection, current) => {
        if (!intersection) {
          return new Set(current);
        }

        return new Set([...intersection].filter((role) => current.has(role)));
      }, null);

    if (shared && shared.size > 0) {
      const available = exclude ? [...shared].filter((role) => role !== exclude) : [...shared];
      const pool = available.length > 0 ? available : [...shared];
      return pool[Math.floor(Math.random() * pool.length)];
    }
  }

  const pool = exclude ? ALL_STATS.filter((stat) => stat !== exclude) : ALL_STATS;
  const finalPool = pool.length > 0 ? pool : ALL_STATS;
  return finalPool[Math.floor(Math.random() * finalPool.length)];
}

export function getNextBattleStat(options: {
  verse?: string | null;
  usedStats?: Array<StatType | null | undefined>;
  currentStat?: StatType | null;
  seed?: string | number | null;
  rolesByVerse?: Record<string, string[]> | null;
}): StatType {
  const stats = getStatsForVerse(options.verse, options.rolesByVerse);
  if (stats.length === 0) return getRandomStat({ exclude: options.currentStat });

  const used = new Set((options.usedStats ?? []).filter(Boolean) as StatType[]);
  let available = stats.filter((stat) => !used.has(stat));

  if (available.length === 0) {
    available = stats.filter((stat) => stat !== options.currentStat);
  }
  if (available.length === 0) available = stats;

  const seedValue = String(options.seed ?? used.size);
  const offset = hashString(seedValue) % available.length;
  return available[offset];
}

const VERSE_ORDER = [
  'Naruto',
  'Bleach',
  'One Piece',
  'One Punch Man',
  'Dragon Ball',
  'Black Clover',
  'My Hero Academia',
  'Solo Leveling',
  '7 Deadly Sins',
  'Jujutsu Kaisen',
  'Demon Slayer',
];

export function getAllVerses(): string[] {
  const known = new Set(VERSE_ORDER);
  const extra = Object.keys(CHARACTERS).filter((v) => !known.has(v)).sort();
  const creatorVerses = [...loadCreatorVerseRows(), ...loadRuntimeCreatorVerseRows()].map((row) => creatorVerseLabel(row));
  return [...VERSE_ORDER.filter((v) => v in CHARACTERS), ...extra, CROSS_DRAFT_VERSE, ...creatorVerses];
}

export function getVerseBackdrop(verse: string): string {
  const creatorVerse = getCreatorVerseByLabel(verse);
  if (creatorVerse) return creatorVerse.backdrop;
  return VERSE_BACKDROPS[verse] ?? ALL_VERSES_BACKDROP;
}

// Small (480px) copy for verse picker tiles; the full backdrop is only for the page background.
// Creator verses and anything outside public/assets/backdrops keep their own image.
export function getVerseThumbnail(verse: string): string {
  const backdrop = getVerseBackdrop(verse);
  const prefix = '/assets/backdrops/';
  return backdrop.startsWith(prefix) && !backdrop.includes('/thumbs/')
    ? `${prefix}thumbs/${backdrop.slice(prefix.length)}`
    : backdrop;
}

function getCreatorVerses() {
  return [...loadCreatorVerseRows(), ...loadRuntimeCreatorVerseRows()].map((row) => adaptCreatorVerse(row));
}

function getCreatorVerseByLabel(verse: string) {
  return getCreatorVerses().find((creatorVerse) => creatorVerse.label === verse) ?? null;
}

function getCreatorCharacters() {
  return getCreatorVerses().flatMap((creatorVerse) => creatorVerse.characters);
}

function getCreatorCharacterById(id: string) {
  return getCreatorCharacters().find((character) => character.id === id) ?? null;
}

function getCharactersForVerse(verse: string): Character[] {
  if (verse === CROSS_DRAFT_VERSE) return [...ALL_CHARACTERS];
  const creatorVerse = getCreatorVerseByLabel(verse);
  if (creatorVerse) return creatorVerse.characters;
  return CHARACTERS[verse] ? [...CHARACTERS[verse]] : [];
}
